import { ScrollDispatcher } from '@angular/cdk/scrolling'
import type { ComponentRef } from '@angular/core'
import { inject, Injectable } from '@angular/core'
import { animationFrameScheduler, catchError, EMPTY, map, merge, switchMap, take, takeUntil, throttleTime } from 'rxjs'

import { observeIntersection } from '@app/core/ui/observe-intersection'
import { observeResize } from '@app/core/ui/observe-resize'
import type { Confirmable } from '@app/modules/ui/models/confirmable'
import { TooltipService } from '@ui/components/tooltip/tooltip.service'

const BACKDROP_HIGHLIGHT_RADIUS = 8

@Injectable({ providedIn: 'root' })
export class ExplainerService {
  private readonly tooltipService = inject(TooltipService)
  private readonly scrollDispatcher = inject(ScrollDispatcher)

  show(
    element: HTMLElement,
    radius: number,
    title: string,
    description: string,
  ): {
    instance: Confirmable
    componentRef: ComponentRef<Confirmable>
  } {
    element.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'center' })
    if (!this.isElementVisible(element)) {
      throw new Error('Element is not currently visible.')
    }

    const { instance, componentRef, updatePosition, backdropRef } = this.tooltipService.show({
      anchor: element,
      title,
      description,
      extraOffset: BACKDROP_HIGHLIGHT_RADIUS,
      backdropStyle: 'dark',
      backdropClickThrough: false,
    })

    backdropRef.setInput('highlightAnchor', element)
    backdropRef.setInput('highlightRadius', radius)
    backdropRef.setInput('highlightOffset', BACKDROP_HIGHLIGHT_RADIUS)

    const elements = [
      element,
      backdropRef.instance.elementRef.nativeElement,
      ...this.scrollDispatcher
        .getAncestorScrollContainers(element)
        .map(scrollable => [...scrollable.getElementRef().nativeElement.children] as HTMLElement[])
        .reduce((acc, cur) => [...acc, ...cur], []),
    ]

    merge(
      // While explainer has "block" scroll strategy it may be still possible to scroll programically
      // which might cause the discrepancy
      this.scrollDispatcher.scrolled(),
      ...elements.map(el => observeResize(el).pipe(catchError(() => EMPTY))),
    )
      .pipe(
        throttleTime(0, animationFrameScheduler),
        switchMap(() =>
          observeIntersection(element).pipe(
            take(1),
            map(entries => entries[0].intersectionRatio < 1),
          ),
        ),
        takeUntil(instance.close$),
      )
      .subscribe(shouldScroll => {
        if (shouldScroll) {
          element.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'center' })
        }
        backdropRef.instance.recalculate()
        updatePosition()
      })

    return {
      componentRef,
      instance,
    }
  }

  private isElementVisible(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect()
    const x = rect.left + rect.width / 2
    const y = rect.top + rect.height / 2
    const topEl = document.elementFromPoint(x, y)

    return element.contains(topEl)
  }
}
